import { Link } from 'react-router-dom';
import styles from './Legal.module.css';
import FinancialDataDisclaimer from './FinancialDataDisclaimer';
import { LEGAL_LAST_UPDATED } from './legalMeta';

// Financial-information / data-accuracy / no-guarantee disclaimer page.
//
// Language is deliberately factual and scoped to what FunViz actually does (a
// free visualization tool — no accounts, no advice, no guarantees). It does
// NOT claim real-time data, data accuracy, or any FMP relationship/right the
// app cannot substantiate. The third-party data notice is rendered via the
// shared provider-neutral <FinancialDataDisclaimer>, which stays neutral until
// data-provider licensing is confirmed (see src/config/dataProvider.js).
const Disclaimer = () => (
  <main className={styles.page}>
    <article className={styles.content}>
      <header className={styles.header}>
        <h1 className={styles.title}>Disclaimer</h1>
        <p className={styles.updated}>Last updated: {LEGAL_LAST_UPDATED}</p>
      </header>

      <section className={styles.section} aria-labelledby="not-advice">
        <h2 id="not-advice" className={styles.sectionHeading}>
          Not financial advice
        </h2>
        <div className={styles.callout}>
          <p>
            FunViz is a visualization and research tool provided for
            informational and educational purposes only. Nothing on this site
            constitutes investment, financial, legal, tax, or accounting advice,
            or a recommendation to buy, sell, or hold any security. You should
            not rely solely on the information presented here when making
            investment decisions, and you should consult a qualified
            professional about your specific circumstances.
          </p>
        </div>
      </section>

      <section className={styles.section} aria-labelledby="data-accuracy">
        <h2 id="data-accuracy" className={styles.sectionHeading}>
          Data accuracy and availability
        </h2>
        <p>
          Market and financial data shown in FunViz are obtained from a
          third-party data provider and are processed and transformed by FunViz
          (for example, currency conversion, trailing-twelve-month figures, and
          fair-value and ratio calculations). This information may be delayed,
          incomplete, or inaccurate, may be subject to later correction or
          restatement, and may be unavailable from time to time.
        </p>
        <p>
          Figures labelled as estimates, implied values, or backtests are
          derived calculations, not statements of fact or forecasts of future
          results. FunViz does not warrant that any data or calculation is
          accurate, complete, current, or fit for any particular purpose.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="backtesting">
        <h2 id="backtesting" className={styles.sectionHeading}>
          Backtested strategies
        </h2>
        <p>
          The Market Health page displays rules-based strategies applied to
          historical S&amp;P 500 data. All strategy results are hypothetical
          backtests &mdash; they reflect the mechanical application of rules to
          past data, not actual trading. Backtested performance does not account
          for trading costs, slippage, taxes, or the behavioral challenges of
          following a model in real time.
        </p>
        <p>
          The strategies are optimized for drawdown protection and
          risk-adjusted return, not return maximization. Historical
          outperformance, where it exists, is largely conditional on the
          occurrence of specific crisis episodes whose recurrence, timing, and
          severity are unknowable in advance. The strategies are calibrated on
          a small number of bear market events and may not perform as expected
          in future market regimes.
        </p>
        <p>
          Strategy signals, scores, and model states are provided for
          informational and educational purposes only. They do not constitute
          buy, sell, or hold recommendations. FunViz accepts no responsibility
          for investment decisions made using these signals.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="no-guarantee">
        <h2 id="no-guarantee" className={styles.sectionHeading}>
          No guarantees
        </h2>
        <p>
          FunViz makes no guarantee regarding investment performance, the
          accuracy of any estimate or forecast, the completeness of any
          financial information, the continued availability of market data, or
          the profitability of any investment decision. Investing involves risk,
          including the possible loss of principal. Past performance does not
          guarantee future results.
        </p>
      </section>

      <section className={styles.section} aria-labelledby="third-party-data">
        <h2 id="third-party-data" className={styles.sectionHeading}>
          Third-party data
        </h2>
        <FinancialDataDisclaimer variant="block" />
      </section>

      <p>
        <Link to="/" className={styles.backLink}>&larr; Back to FunViz</Link>
      </p>
    </article>
  </main>
);

export default Disclaimer;
